import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton
} from '@mui/material';
import { Add, Dashboard, Delete, Edit, Logout } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://8ozejfxzg4.execute-api.us-east-1.amazonaws.com/api';

// Comment input component to prevent re-renders
const CommentInput = ({ projectId, moduleId, initialValue, onSave, readOnly = false }) => {
  const [value, setValue] = useState(initialValue || '');
  
  useEffect(() => {
    setValue(initialValue || '');
  }, [initialValue]);

  if (readOnly) {
    return (
      <Box sx={{ minWidth: '200px', padding: '8px' }}>
        <Typography variant="body2" color="text.secondary">
          {value || 'No comments'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <textarea
        rows={2}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add comments..."
        style={{ 
          minWidth: '200px', 
          padding: '8px', 
          border: '1px solid #ccc', 
          borderRadius: '4px',
          fontFamily: 'inherit',
          fontSize: '14px',
          resize: 'vertical'
        }}
      />
      <Button
        variant="contained"
        size="small"
        onClick={() => onSave(projectId, moduleId, value)}
        sx={{ mt: 0.5 }}
      >
        Save
      </Button>
    </Box>
  );
};

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // Existing state
  const [tabValue, setTabValue] = useState(0);
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [usage, setUsage] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  // Dialog states
  const [editProjectDialog, setEditProjectDialog] = useState(false);
  const [editModuleDialog, setEditModuleDialog] = useState(false);
  const [comments, setComments] = useState({});
  const [editingProject, setEditingProject] = useState(null);
  const [editingModule, setEditingModule] = useState(null);

  // Dialog states
  const [projectDialog, setProjectDialog] = useState(false);
  const [moduleDialog, setModuleDialog] = useState(false);

  // Form states
  const [projectForm, setProjectForm] = useState({ project_name: '', description: '' });
  const [moduleForm, setModuleForm] = useState({ module_name: '', description: '', version: '' });

  useEffect(() => {
    const authStatus = localStorage.getItem('arc-authenticated');
    const storedRole = localStorage.getItem('user-role');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      setUserRole(storedRole || '');
      fetchData();
    }
  }, []);

  useEffect(() => {
    // Initialize comments state when usage data loads
    if (usage.length > 0) {
      const initialComments = {};
      usage.forEach(u => {
        const key = `${u.project_id}-${u.module_id}`;
        initialComments[key] = u.comments || '';
      });
      setComments(initialComments);
    }
  }, [usage]);

  const handleLogin = async () => {
    try {
      const response = await axios.post(`${API_BASE}/login`, loginForm);
      if (response.data.success) {
        setIsAuthenticated(true);
        setUserRole(response.data.role);
        localStorage.setItem('arc-authenticated', 'true');
        localStorage.setItem('user-role', response.data.role);
        setLoginError('');
        fetchData();
        showAlert(`${response.data.message} - Role: ${response.data.role}`, 'success');
      }
    } catch (error) {
      if (error.response && error.response.data) {
        setLoginError(error.response.data.message);
      } else {
        setLoginError('Login failed. Please try again.');
      }
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserRole('');
    localStorage.removeItem('arc-authenticated');
    localStorage.removeItem('user-role');
    setLoginForm({ username: '', password: '' });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [projectsRes, modulesRes, usageRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/projects`),
        axios.get(`${API_BASE}/modules`),
        axios.get(`${API_BASE}/usage`),
        axios.get(`${API_BASE}/stats`)
      ]);
      
      setProjects(projectsRes.data);
      setModules(modulesRes.data);
      setUsage(usageRes.data);
      setStats(statsRes.data);
    } catch (error) {
      showAlert('Error fetching data', 'error');
    }
    setLoading(false);
  };

  const showAlert = (message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: 'success' }), 3000);
  };

  const handleAddProject = async () => {
    try {
      await axios.post(`${API_BASE}/projects`, projectForm);
      setProjectDialog(false);
      setProjectForm({ project_name: '', description: '' });
      fetchData();
      showAlert('Project added successfully');
    } catch (error) {
      showAlert('Error adding project', 'error');
    }
  };

  const handleAddModule = async () => {
    try {
      await axios.post(`${API_BASE}/modules`, moduleForm);
      setModuleDialog(false);
      setModuleForm({ module_name: '', description: '', version: '' });
      fetchData();
      showAlert('Module added successfully');
    } catch (error) {
      showAlert('Error adding module', 'error');
    }
  };

  const handleUsageToggle = async (project_id, module_id, usage_status) => {
    try {
      await axios.post(`${API_BASE}/usage`, { project_id, module_id, usage_status });
      fetchData();
      showAlert('Usage updated successfully');
    } catch (error) {
      showAlert('Error updating usage', 'error');
    }
  };

  // Usage percentage calculation
  const calculateUsagePercentage = (projectId) => {
    const projectUsage = usage.filter(u => u.project_id === projectId);
    if (projectUsage.length === 0) return 0;
    
    const yesCount = projectUsage.filter(u => u.usage_status === 'YES').length;
    const noCount = projectUsage.filter(u => u.usage_status === 'NO').length;
    const totalCount = yesCount + noCount; // Only YES + NO, excluding NA
    
    return totalCount > 0 ? Math.round((yesCount / totalCount) * 100) : 0;
  };

  const handleProjectNameClick = (projectId) => {
    setSelectedProject(projectId);
    setTabValue(4); // Switch to Project Details tab
  };

  const handleCommentsUpdate = useCallback(async (projectId, moduleId, comments) => {
    try {
      const usageRecord = usage.find(u => u.project_id === projectId && u.module_id === moduleId);
      if (usageRecord) {
        await axios.post(`${API_BASE}/usage`, { 
          project_id: projectId, 
          module_id: moduleId, 
          usage_status: usageRecord.usage_status,
          comments: comments
        });
        fetchData();
        showAlert('Comments saved successfully');
      }
    } catch (error) {
      showAlert('Error saving comments', 'error');
    }
  }, [usage]);

  const handleCommentChange = useCallback((projectId, moduleId, value) => {
    const key = `${projectId}-${moduleId}`;
    setComments(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveComments = useCallback(async (projectId, moduleId) => {
    const key = `${projectId}-${moduleId}`;
    const commentValue = comments[key] || '';
    await handleCommentsUpdate(projectId, moduleId, commentValue);
  }, [comments, handleCommentsUpdate]);

  // CRUD operations
  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await axios.delete(`${API_BASE}/projects/${projectId}`);
        fetchData();
        showAlert('Project deleted successfully');
      } catch (error) {
        showAlert('Error deleting project', 'error');
      }
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (window.confirm('Are you sure you want to delete this module?')) {
      try {
        await axios.delete(`${API_BASE}/modules/${moduleId}`);
        fetchData();
        showAlert('Module deleted successfully');
      } catch (error) {
        showAlert('Error deleting module', 'error');
      }
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectForm({ project_name: project.project_name, description: project.description });
    setEditProjectDialog(true);
  };

  const handleUpdateProject = async () => {
    try {
      await axios.put(`${API_BASE}/projects/${editingProject.project_id}`, projectForm);
      setEditProjectDialog(false);
      setProjectForm({ project_name: '', description: '' });
      fetchData();
      showAlert('Project updated successfully');
    } catch (error) {
      showAlert('Error updating project', 'error');
    }
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setModuleForm({ module_name: module.module_name, description: module.description, version: module.version });
    setEditModuleDialog(true);
  };

  const handleUpdateModule = async () => {
    try {
      await axios.put(`${API_BASE}/modules/${editingModule.module_id}`, moduleForm);
      setEditModuleDialog(false);
      setModuleForm({ module_name: '', description: '', version: '' });
      fetchData();
      showAlert('Module updated successfully');
    } catch (error) {
      showAlert('Error updating module', 'error');
    }
  };

  const getUsageForProject = (project_id) => {
    return usage.filter(u => u.project_id === project_id);
  };

  const getProjectUsagePercentage = (projectId) => {
    const projectUsages = usage.filter(u => u.project_id === projectId);
    const yesCount = projectUsages.filter(u => u.usage_status === 'YES').length;
    const noCount = projectUsages.filter(u => u.usage_status === 'NO').length;
    const totalRelevant = yesCount + noCount;
    return totalRelevant > 0 ? ((yesCount / totalRelevant) * 100).toFixed(1) : '0.0';
  };

  const getModuleUsageStatus = (project_id, module_id) => {
    const usageItem = usage.find(u => u.project_id === project_id && u.module_id === module_id);
    return usageItem ? usageItem.usage_status : 'NA';
  };

  const projectModules = useMemo(() => {
    if (!selectedProject) return [];
    
    const projectUsages = usage.filter(u => u.project_id === selectedProject && (u.usage_status === 'YES' || u.usage_status === 'NO'));
    return projectUsages.map(u => {
      const module = modules.find(m => m.module_id === u.module_id);
      return {
        module_name: module ? module.module_name : u.module_id,
        usage_status: u.usage_status,
        comments: u.comments || '',
        project_id: u.project_id,
        module_id: u.module_id
      };
    });
  }, [selectedProject, usage, modules]);

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <>
      {!isAuthenticated ? (
        <Container maxWidth="sm" sx={{ mt: 8 }}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                <img src="/logo.png" alt="Logo" style={{ height: '60px', marginBottom: '16px' }} />
                <Typography variant="h5" component="h1" gutterBottom>
                  IAC Usage Tracker
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please sign in to continue
                </Typography>
              </Box>
              
              <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
                <TextField
                  fullWidth
                  label="Username"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  margin="normal"
                  required
                />
                {loginError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {loginError}
                  </Alert>
                )}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                >
                  Sign In
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      ) : (
        <Container maxWidth="xl">
          {alert.show && (
            <Alert severity={alert.type} sx={{ mb: 2 }}>
              {alert.message}
            </Alert>
          )}

          {/* Header with Logo and Logout */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="flex-end">
              <img src="/logo.png" alt="Logo" style={{ height: '50px', marginRight: '12px' }} />
              <Typography variant="h5" component="h1">
                IAC Usage Tracker
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Logout />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<Dashboard />} label="Dashboard" />
          <Tab label="Projects" />
          <Tab label="Modules" />
          <Tab label="Usage Matrix" />
          <Tab label="Project Details" />
        </Tabs>
      </Box>

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {/* Dashboard Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => setTabValue(1)}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Projects</Typography>
                <Typography variant="h4">{stats.totalProjects || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => setTabValue(2)}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Modules</Typography>
                <Typography variant="h4">{stats.totalModules || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Active Usages (YES)</Typography>
                <Typography variant="h4">{stats.totalUsages || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Usage %</Typography>
                <Typography variant="h4" color="primary">{stats.overallUsagePercentage || 0}%</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={fetchData}>
            Refresh Data
          </Button>
        </Box>
      </TabPanel>

      {/* Projects Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 2 }}>
          {userRole === 'admin' && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setProjectDialog(true)}
            >
              Add Project
            </Button>
          )}
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Usage %</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project.project_id}>
                  <TableCell>
                    <Button 
                      variant="text" 
                      onClick={() => handleProjectNameClick(project.project_id)}
                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto', color: 'primary.main' }}
                    >
                      {project.project_name}
                    </Button>
                  </TableCell>
                  <TableCell>{project.description}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${calculateUsagePercentage(project.project_id)}%`}
                      color={parseFloat(calculateUsagePercentage(project.project_id)) > 50 ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{new Date(project.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {userRole === 'admin' && (
                      <>
                        <IconButton onClick={() => handleEditProject(project)} size="small">
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteProject(project.project_id)} size="small" color="error">
                          <Delete />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Modules Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 2 }}>
          {userRole === 'admin' && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setModuleDialog(true)}
            >
              Add Module
            </Button>
          )}
        </Box>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Module Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.map((module) => (
                <TableRow key={module.module_id}>
                  <TableCell>{module.module_name}</TableCell>
                  <TableCell>{module.description}</TableCell>
                  <TableCell>{module.version}</TableCell>
                  <TableCell>{new Date(module.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {userRole === 'admin' && (
                      <>
                        <IconButton onClick={() => handleEditModule(module)} size="small">
                          <Edit />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteModule(module.module_id)} size="small" color="error">
                          <Delete />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Usage Matrix Tab */}
      <TabPanel value={tabValue} index={3}>
        <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 2, fontWeight: 'bold', minWidth: 200 }}>
                  Project
                </TableCell>
                {modules.map((module) => (
                  <TableCell 
                    key={module.module_id} 
                    align="center" 
                    sx={{ 
                      minWidth: 120, 
                      fontWeight: 'bold',
                      backgroundColor: 'white'
                    }}
                  >
                    {module.module_name.replace('terraform-aws-arc-', '')}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow key={project.project_id}>
                  <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 1, fontWeight: 'bold' }}>
                    {project.project_name}
                  </TableCell>
                  {modules.map((module) => (
                    <TableCell key={module.module_id} align="center">
                      <ToggleButtonGroup
                        value={getModuleUsageStatus(project.project_id, module.module_id)}
                        exclusive
                        onChange={(e, newValue) => {
                          if (newValue !== null && userRole === 'admin') {
                            handleUsageToggle(project.project_id, module.module_id, newValue);
                          }
                        }}
                        size="small"
                        disabled={userRole !== 'admin'}
                      >
                        <ToggleButton value="YES" color="success">
                          YES
                        </ToggleButton>
                        <ToggleButton value="NO" color="error">
                          NO
                        </ToggleButton>
                        <ToggleButton value="NA" color="standard">
                          NA
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Project Details Tab */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth sx={{ maxWidth: 400 }}>
            <InputLabel>Select Project</InputLabel>
            <Select
              value={selectedProject}
              label="Select Project"
              onChange={(e) => setSelectedProject(e.target.value)}
            >
              {projects.map((project) => (
                <MenuItem key={project.project_id} value={project.project_id}>
                  {project.project_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {selectedProject && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Terraform Modules in {projects.find(p => p.project_id === selectedProject)?.project_name}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {projectModules.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Module Name</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Comments</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectModules.map((moduleData, index) => (
                          <TableRow key={`${moduleData.project_id}-${moduleData.module_id}`}>
                            <TableCell>{moduleData.module_name.replace('terraform-aws-arc-', '')}</TableCell>
                            <TableCell>
                              <Chip 
                                label={moduleData.usage_status} 
                                color={moduleData.usage_status === 'YES' ? 'success' : 'error'} 
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <CommentInput
                                projectId={moduleData.project_id}
                                moduleId={moduleData.module_id}
                                initialValue={moduleData.comments}
                                onSave={handleCommentsUpdate}
                                readOnly={userRole !== 'admin'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">
                    No modules are currently being used in this project.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      {/* Add Project Dialog */}
      <Dialog open={projectDialog} onClose={() => setProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={projectForm.project_name}
            onChange={(e) => setProjectForm({...projectForm, project_name: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={projectForm.description}
            onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectDialog(false)}>Cancel</Button>
          <Button onClick={handleAddProject} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Add Module Dialog */}
      <Dialog open={moduleDialog} onClose={() => setModuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Module</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Module Name"
            fullWidth
            variant="outlined"
            value={moduleForm.module_name}
            onChange={(e) => setModuleForm({...moduleForm, module_name: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={moduleForm.description}
            onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Version"
            fullWidth
            variant="outlined"
            value={moduleForm.version}
            onChange={(e) => setModuleForm({...moduleForm, version: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialog(false)}>Cancel</Button>
          <Button onClick={handleAddModule} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={editProjectDialog} onClose={() => setEditProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Project Name"
            fullWidth
            variant="outlined"
            value={projectForm.project_name}
            onChange={(e) => setProjectForm({...projectForm, project_name: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={projectForm.description}
            onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditProjectDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateProject} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Module Dialog */}
      <Dialog open={editModuleDialog} onClose={() => setEditModuleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Module</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Module Name"
            fullWidth
            variant="outlined"
            value={moduleForm.module_name}
            onChange={(e) => setModuleForm({...moduleForm, module_name: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={moduleForm.description}
            onChange={(e) => setModuleForm({...moduleForm, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Version"
            fullWidth
            variant="outlined"
            value={moduleForm.version}
            onChange={(e) => setModuleForm({...moduleForm, version: e.target.value})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModuleDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateModule} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

        </Container>
      )}
    </>
  );
}

export default App;
